import {
  type KeyboardEvent,
  type KeyboardEventHandler,
  type MouseEvent,
  type MouseEventHandler,
  useCallback,
  useSyncExternalStore,
} from 'react'

import {isSomebodyElsesEvent} from '../cells/own-card-event'
import {isOwnKeyboardTarget} from '../focus/keys'
import {useBoardSelectionStore} from './contexts'
import type {BoardSelectedCard, BoardSelectionIntent} from './types'

export type BoardCardSelection = {
  isSelected: boolean
  selectCard: (intent: BoardSelectionIntent) => void
  onClick: MouseEventHandler<HTMLElement>
  onMouseDown: MouseEventHandler<HTMLElement>
  onKeyDown: KeyboardEventHandler<HTMLElement>
}

/**
 * One card's share of the board's selection: whether it is selected, and the gesture
 * that changes that.
 *
 * A boolean rather than the selection, and that is the point: every card on the board
 * subscribes to this, and a gesture has to wake only the ones whose answer changed. A
 * snapshot returning the selection itself would compare unequal every time and wake all
 * of them.
 *
 * `card` must be stable across renders, because it keys the subscription, and a fresh
 * object every render would rebuild it every render.
 */
export function useBoardCardSelection(card: BoardSelectedCard): BoardCardSelection {
  const {isSelected, subscribe, selectCard} = useBoardSelectionStore()

  const getSnapshot = useCallback(() => isSelected(card), [isSelected, card])
  // The same snapshot on the server: it is read from the selection the caller stated,
  // which is the same list on both sides.
  const selected = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const select = useCallback((intent: BoardSelectionIntent) => selectCard(card, intent), [selectCard, card])

  const onClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (event.defaultPrevented) return
      if (isSomebodyElsesEvent(event.target, event.currentTarget)) return

      const intent = intentFor(event)
      if (intent === null) return

      select(intent)
    },
    [select],
  )

  /*
    Shift-clicking is how a browser extends the document's text selection, so selecting a
    range of cards would also drag a highlight across every word between the two of them,
    and the reader would be left with a page-wide smear they did not ask for and have to
    click again to clear.

    Stopped at `mousedown`, because that is the event the highlight is extended on;
    clearing it afterwards would mean drawing it first. Only for the gesture that has the
    problem: an ordinary click still selects text in a card, which is how a title gets
    copied.

    Preventing that default also prevents the focus a click would have landed, and a click
    on a card is how a reader moves the board's position onto it, so focus is landed here
    instead.
  */
  const onMouseDown = useCallback((event: MouseEvent<HTMLElement>) => {
    if (event.defaultPrevented || !event.shiftKey) return
    if (isSomebodyElsesEvent(event.target, event.currentTarget)) return

    event.preventDefault()
    event.currentTarget.focus()
  }, [])

  /*
    The keyboard's half of the same gesture, on the card rather than on the board's root.

    It belongs here because the card focus is on is the card the gesture is about, and
    because the root's handler returns on every modifier, which is what keeps `Shift` the
    range gesture's and leaves a modifier-driven arrow free for
    https://github.com/github/views/issues/43. Nothing in that guard is relaxed for this.

    `Space` toggles and `Shift+Space` runs a range, which is what ARIA's own multi-select
    listbox pattern says those two keys do, and what a keyboard reader arriving from any
    file manager expects. Toggling rather than replacing is what makes it the equal of the
    pointer gesture: arrow to a card, press `Space`, and a second card joins the first,
    where a plain replace would leave a keyboard unable to select two of anything.

    `Cmd`, `Ctrl` and `Alt` are left alone deliberately. `Cmd+Space` is Spotlight and
    `Ctrl+Space` is an input-method switch on several platforms, so a board taking either
    would be taking a key its reader cannot give it.
  */
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      /*
        Read here rather than through `@github-ui/ui-commands`, for the reasons the board's
        arrow keys are: this is the widget's own key inside a roving tabindex rather than
        an application command anybody would rebind, and a packaging test pins this
        package's dependencies, which that library is not among.
      */
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.defaultPrevented || event.key !== ' ') return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.altKey || event.ctrlKey || event.metaKey) return
      if (isSomebodyElsesEvent(event.target, event.currentTarget)) return
      // A caller draws what it likes in a card, and `Space` belongs to anything in there
      // that types before it belongs to the board.
      if (isOwnKeyboardTarget(event.target)) return

      // `Space` scrolls the nearest scroll container, which is the cell this card is in,
      // so the gesture would page the cell out from under the reader as well as select.
      event.preventDefault()
      select(event.shiftKey ? 'range' : 'toggle')
    },
    [select],
  )

  return {isSelected: selected, selectCard: select, onClick, onMouseDown, onKeyDown}
}

/**
 * What a click on a card means, or `null` for a click that is somebody else's.
 *
 * `Shift` is a range and is claimed here rather than by any key, which is why the board's
 * own `onKeyDown` leaves every modifier alone. `Alt` belongs to the platform -- it is how
 * a link is downloaded rather than followed -- so a click holding it is not a gesture
 * this board takes.
 *
 * Read here rather than through `@github-ui/ui-commands`, for the two reasons the board's
 * arrow keys are: this is the widget's own pointer gesture rather than an application
 * command anybody would rebind, and there is no key to bind in the first place. A
 * packaging test pins this package's dependencies too, and that library is not among them.
 */
function intentFor(event: MouseEvent<HTMLElement>): BoardSelectionIntent | null {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  if (event.altKey) return null
  if (event.shiftKey) return 'range'

  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  return event.ctrlKey || event.metaKey ? 'toggle' : 'replace'
}
